'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useState, useRef, useEffect } from 'react';
import { Icon } from './Icon';
import { useSatelliteNavigationOptional } from '@to/ui';

export type SortOption = 'featured' | 'a-z' | 'z-a';

const SORT_LABELS: Record<SortOption, string> = {
  featured: 'Featured',
  'a-z': 'A to Z',
  'z-a': 'Z to A',
};

const SORT_OPTIONS: SortOption[] = ['featured', 'a-z', 'z-a'];

export interface SortDropdownProps {
  /** Base path for building URLs. Default: '/vendors' */
  vendorsPath?: string;
  /** Currently active sort. Default: 'featured' */
  currentSort?: SortOption;
  /**
   * Which sort options to offer. Default: all. Callers can pass
   * `['a-z', 'z-a']` when only alphabetical sorting is supported.
   */
  options?: SortOption[];
}

export function SortDropdown({
  vendorsPath = '/vendors',
  currentSort = 'featured',
  options = SORT_OPTIONS,
}: SortDropdownProps) {
  const router = useRouter();
  const navigation = useSatelliteNavigationOptional();
  const searchParams = useSearchParams();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(option: SortOption) {
    setOpen(false);
    const params = new URLSearchParams(searchParams.toString());
    // Reset to page 1 when changing sort
    params.delete('page');
    if (option === 'featured') {
      params.delete('sort');
    } else {
      params.set('sort', option);
    }
    const qs = params.toString();
    const targetHref = qs ? `${vendorsPath}?${qs}` : vendorsPath;
    const started =
      navigation?.beginNavigation({
        routeTemplate: '/vendors',
        navigationType: 'data_action',
        targetKey: targetHref,
        targetHref,
      }) ?? true;
    if (started) router.push(targetHref);
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        disabled={navigation?.isPending}
        aria-busy={navigation?.isPending}
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors duration-150"
        style={{
          borderColor: 'var(--brand-card-border)',
          color: 'var(--brand-text)',
          backgroundColor: 'var(--brand-card-bg, white)',
          fontFamily: 'var(--brand-font-body)',
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ color: 'var(--brand-text-muted)' }}>Sort:</span>
        {SORT_LABELS[currentSort]}
        <Icon
          name={open ? 'expand_less' : 'expand_more'}
          size={18}
          style={{ color: 'var(--brand-text-muted)' }}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full z-40 mt-1 min-w-[160px] overflow-hidden rounded-lg border shadow-lg"
          style={{
            borderColor: 'var(--brand-card-border)',
            backgroundColor: 'var(--brand-card-bg, white)',
          }}
          role="listbox"
        >
          {options.map((option) => (
            <button
              disabled={navigation?.isPending}
              key={option}
              onClick={() => handleSelect(option)}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-[var(--brand-surface-subtle)]"
              style={{
                color: option === currentSort ? 'var(--brand-primary)' : 'var(--brand-text)',
                fontWeight: option === currentSort ? 600 : 400,
                fontFamily: 'var(--brand-font-body)',
              }}
              role="option"
              aria-selected={option === currentSort}
            >
              {option === currentSort && (
                <Icon name="check" size={16} style={{ color: 'var(--brand-primary-text, var(--brand-primary))' }} />
              )}
              <span className={option !== currentSort ? 'pl-6' : ''}>
                {SORT_LABELS[option]}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
